import React, { useEffect, useState } from 'react';
import Input from '../../components/input/Input';
import { formatDate } from '../../utils/dateUtils';
import useCustomerProfile from './useCustomerProfile';
import CustomerManageModal from './CustomerManageModal';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { validateNumberInput, numberFields } from '../../utils/formValidations';
import { useSelector } from 'react-redux';
import FutureRateModal from './FutureRateModal';
import CustomerRateCheckModal from './CustomerRateCheckModal';
import CustomerPaymentHistoryModal from './CustomerPaymentHistoryModal';
import PromoteLinkModal from './PromoteLinkModal';
import MergeAccountModal from './MergeAccountModal';
import CustomerChangeLogModal from './CustomerChangeLogModal';
import NegotiationQuotaModal from './NegotiationQuotaModal';
import WebsiteFeeExclusionService from '../setting/websites/WebsiteFeeExclusionService';
import CustomerProfileService from './CustomerProfileService';
import { AUCTION_NOT_READY_TEXT } from './auctionReadinessText';

// Ready-to-send chat message for a customer whose LINE isn't on their account (or who
// hasn't added / has blocked us). The link signs them in / connects LINE; the dashboard
// it lands on asks a non-friend to add us.
const connectLineMessage = (url) =>
  `ก่อนเริ่มประมูล รบกวนกดลิงก์นี้เพื่อเชื่อมต่อ LINE กับบัญชีทามะจังนะคะ ระบบจะแจ้งผลประมูลและยอดที่ต้องชำระทาง LINE ค่ะ\n${url}`;

const copyConnectLineMessage = async (url) => {
  try {
    await navigator.clipboard.writeText(connectLineMessage(url));
    toast.success('คัดลอกแล้ว');
  } catch {
    toast.error('คัดลอกไม่สำเร็จ');
  }
};

// Header status pill — one size and shape for every status, colour = meaning.
const PILL_TONES = {
  green: 'bg-green-50 text-green-700 border-green-300',
  amber: 'bg-amber-50 text-amber-700 border-amber-300',
  red: 'bg-red-50 text-red-700 border-red-300',
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-300',
  gray: 'bg-gray-50 text-gray-600 border-gray-300',
};
const Pill = ({ tone, children }) => (
  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold border rounded-full ${PILL_TONES[tone]}`}>
    {children}
  </span>
);

// Secondary header actions: quiet text buttons on one line under the title.
const LinkButton = ({ onClick, children }) => (
  <button type="button" onClick={onClick} className="hover:text-gray-900 hover:underline">
    {children}
  </button>
);
const Dot = () => <span className="text-gray-300">·</span>;

const defaultProfile = {
  name: '',
  phone: '',
  smileName: '',
  currentSmileName: '',
  contact: '',
  channel: '',
  address: '',
  productRate: '',
  weightRate: '',
  isFlowRate: false, 
  extraFlowDiscount: '', 
  minimumAirWeight: '',
};

const ProfileDetail = ({ profile, accumulatedOrderAmount, onAccountChanged }) => {
  const [isHidden, setIsHidden] = useState(true);
  const [profileData, setProfileData] = useState(defaultProfile)
  const [isEditMode, setIsEditMode] = useState(false);
  const [isRateModalOpen, setIsRateModalOpen] = useState(false);
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [isRateCheckOpen, setIsRateCheckOpen] = useState(false);
  const [isPaymentHistoryOpen, setIsPaymentHistoryOpen] = useState(false);
  const [isPromoteLinkOpen, setIsPromoteLinkOpen] = useState(false);
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [isChangeLogOpen, setIsChangeLogOpen] = useState(false);
  const [isNegotiationQuotaOpen, setIsNegotiationQuotaOpen] = useState(false);
  const [feeExclusions, setFeeExclusions] = useState([]);
  const shopName = useSelector((state) => state.shop.name);
  const { authState } = useAuth();
  const isSuperuser = authState?.user?.role === 'superuser';

  const {updateProfile} = useCustomerProfile()

  // Platform customers (registered via the website) have a linked User account;
  // walk-in profiles do not. Only they own a CustomerAddress book.
  const isPlatformUser = !!profile?.userId;

  // LINE on the ACCOUNT (User.lineUserId, connected by the customer on the platform) is
  // what lets us message them about their wallet. A profile.socialId alone only names the
  // chat we talk to — not an account — so it gets its own, weaker chip.
  const isAccountLineConnected = !!profile?.User?.lineUserId;
  const isChatLineOnly = !isAccountLineConnected && !!profile?.socialId;

  // LINE reachability: lineBlocked = a friend who blocked/left us (chase them);
  // lineFriend === true = confirmed reachable friend. Mutually exclusive.
  const lineBlocked = !!profile?.User?.lineBlocked;
  const lineFriend = profile?.User?.lineFriend === true;

  // Auction readiness: checked automatically (live, silent LINE check) for platform
  // customers; a profile without a login can never be ready, so no call for it.
  const [auctionReadiness, setAuctionReadiness] = useState(null);
  useEffect(() => {
    if (!profile?.id) {
      setAuctionReadiness(null);
      return undefined;
    }
    if (!profile.userId) {
      setAuctionReadiness({ ready: false, reason: 'not_platform' });
      return undefined;
    }
    let cancelled = false;
    setAuctionReadiness(null);
    CustomerProfileService.getAuctionReadiness(profile.id)
      .then(res => { if (!cancelled) setAuctionReadiness(res.data); })
      .catch(() => { if (!cancelled) setAuctionReadiness(null); });
    return () => { cancelled = true; };
  }, [profile?.id, profile?.userId]);

  // One LINE pill instead of separate connected / friend / blocked chips. The readiness
  // check just asked LINE live, so its answer wins over the cached flags on the profile.
  const liveReason = auctionReadiness?.reason;
  const isBlocked = liveReason === 'line_blocked' || (lineBlocked && !auctionReadiness?.ready);
  const isFriend = !!auctionReadiness?.ready || (lineFriend && liveReason !== 'line_not_friend');
  const lineStatus = isBlocked
    ? { tone: 'red', text: '🚫 LINE บล็อก' }
    : isAccountLineConnected
      ? isFriend
        ? { tone: 'green', text: 'LINE ✓ บัญชี · เพื่อน' }
        : { tone: 'amber', text: 'LINE บัญชี · ยังไม่เพิ่มเพื่อน' }
      : isChatLineOnly
        ? { tone: 'amber', text: 'LINE (แชท)' }
        : { tone: 'gray', text: 'ไม่มี LINE' };

  const toggleHidden = () => {
    setIsHidden(!isHidden);
  };

  const handleInputChange = (name, value) => {
    if (numberFields.has(name)) {
			value = validateNumberInput(value, profileData[name]);
		}
    setProfileData(prevState => ({
      ...prevState,
      [name]: value
    }));
  }

  const handleUpdateProfile = async () => {
    // Destructure the Orders property out and capture the rest of the profileData object
    const { Orders, ...dataWithoutOrders } = profileData;
    console.log(dataWithoutOrders)
    // Now send dataWithoutOrders to the backend instead of profileData
    const result = await updateProfile(dataWithoutOrders);
    if (result) {
      console.log(dataWithoutOrders); // Log the data without Orders
      toast.success("Update Successful");
    } else {
      toast.error("Update Failed");
    }
  };

  const toggleEditMode = () => {
    if(isEditMode){
      handleUpdateProfile()
    }
    setIsEditMode(!isEditMode);
  };

  useEffect(()=>{
    if (profile) {
      let updatedProfile = { ...profile, currentSmileName: profile.smileName }; // Set currentSmileName from the initial profile data

      if (profile.CustomerAddresses && profile.CustomerAddresses.length > 0) {
        const firstAddress = profile.CustomerAddresses[0];
        updatedProfile = {
          ...updatedProfile,
          address: firstAddress.address,
          name: firstAddress.name,
        };
      }
      setProfileData(updatedProfile);

      WebsiteFeeExclusionService.getExclusionsByCustomer(profile.id)
        .then(res => setFeeExclusions(res.data || []))
        .catch(() => setFeeExclusions([]));
    }
  },[profile]);
  

  return (
    <div>
      <div className="flex justify-between items-start">
        <div>
          <div className="flex flex-wrap items-center gap-1.5 py-1">
            <h3 className="mr-1 text-2xl font-bold">Profile</h3>
            {isPlatformUser && <Pill tone="indigo">แพลตฟอร์ม</Pill>}
            {profile && <Pill tone={lineStatus.tone}>{lineStatus.text}</Pill>}
            {auctionReadiness && (
              <Pill tone={auctionReadiness.ready ? 'green' : 'red'}>
                {auctionReadiness.ready
                  ? '✅ พร้อมประมูล'
                  : `❌ ประมูล: ${AUCTION_NOT_READY_TEXT[auctionReadiness.reason] || auctionReadiness.reason}`}
                {auctionReadiness.connectLineUrl && (
                  <button
                    type="button"
                    title="คัดลอกข้อความเชื่อม LINE ส่งให้ลูกค้า"
                    onClick={() => copyConnectLineMessage(auctionReadiness.connectLineUrl)}
                    className="ml-1.5 hover:scale-110"
                  >
                    📋
                  </button>
                )}
              </Pill>
            )}
            {profileData?.note && <span className="text-sm text-red-500">⚠ มีหมายเหตุ</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
        <div className="mt-2 -mr-3 flex gap-2">
          {/* Used every day — kept as buttons where admins expect them. */}
          {profile && (
            <>
              <button
                type="button"
                onClick={() => setIsPaymentHistoryOpen(true)}
                className="mx-1 px-4 py-1 bg-slate-700 hover:bg-slate-800 text-white rounded-md"
              >
                ประวัติสลิป
              </button>
              <button
                type="button"
                onClick={() => setIsRateCheckOpen(true)}
                className="mx-1 px-4 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md"
              >
                เช็คยอดสะสม/เรท
              </button>
            </>
          )}
          {!isHidden &&
            <button
              onClick={toggleEditMode}
              className={`mx-1 px-2 py-1 w-24 text-white rounded-md ${isEditMode ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'}`}
            >
              {isEditMode ? 'Save' : 'Edit'}
            </button>
          }
          { !isEditMode &&
            <button
              onClick={toggleHidden}
              className="mx-1 px-4 py-1 w-24 bg-blue-500 hover:bg-blue-600 text-white rounded-md"
            >
              {isHidden ? '+' : '-'} Detail
            </button>
          }
        </div>
          {/* Less-used actions: links under the buttons, right-aligned. */}
          {profile && (
            <div className="flex flex-wrap items-center justify-end gap-x-1 text-sm text-gray-500">
              <LinkButton onClick={() => setIsChangeLogOpen(true)}>ประวัติแก้ไข</LinkButton>
              {isPlatformUser ? (
                <>
                  <Dot />
                  <LinkButton onClick={() => setIsManageModalOpen(true)}>ข้อมูลลูกค้าแพลตฟอร์ม</LinkButton>
                  {/* Only platform customers can negotiate (it needs a login). */}
                  <Dot />
                  <LinkButton onClick={() => setIsNegotiationQuotaOpen(true)}>สิทธิ์ขอลดราคา</LinkButton>
                </>
              ) : (
                <>
                  <Dot />
                  <LinkButton onClick={() => setIsPromoteLinkOpen(true)}>เชื่อมเข้าแพลตฟอร์ม</LinkButton>
                  {isSuperuser && (
                    <>
                      <Dot />
                      <LinkButton onClick={() => setIsMergeOpen(true)}>รวมบัญชีที่สมัครเอง</LinkButton>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4 pt-3 border-b border-gray-900/10 pb-6">
        <Input
          columnSpan={1}
          title="Contact"
          name="contact"
          value={profileData?.contact || ''}
          onChange={(value)=>handleInputChange('contact',value)}
          disable={!isEditMode}
        />
        <Input
          columnSpan={1}
          title="Customer Name"
          name="customerName"
          value={profileData?.customerName || ''}
          onChange={(value)=>handleInputChange('customerName',value)}
          disable={!isEditMode}
        />
        <Input
          columnSpan={1}
          title="Phone"
          name="phone"
          value={profileData?.phone || ''}
          onChange={(value)=>handleInputChange('phone',value)}
          disable={!isEditMode}
        />
        <Input
          columnSpan={1}
          title="Smile Name"
          name="smileName"
          value={profileData?.smileName || ''}
          onChange={(value)=>handleInputChange('smileName',value)}
          disable={!isEditMode}
        />
          {!isHidden && (
            <>
            <Input
              columnSpan={1}
              title="Smile Name Japan"
              name="smileNameJp"
              value={profileData?.smileNameJp || ''}
              onChange={(value)=>handleInputChange('smileNameJp',value)}
              disable={!isEditMode}
            />
            <div className={`col-span-1`}>
              <label className="pl-1 block text-sm font-medium leading-6 text-gray-900">Channel</label>
              <select
                name="channel"
                value={profileData?.channel || ''}
                onChange={(e) => handleInputChange('channel', e.target.value)}
                disabled={!isEditMode}
                className={`w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
                  !isEditMode ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                }`}
              >
                <option value="">-- Select Channel --</option>
                <option value="Line">Line</option>
                <option value="Facebook">Facebook</option>
              </select>
            </div>
            <Input
              columnSpan={1}
              title="Product Rate"
              name="productRate"
              value={profileData?.productRate || ''}
              onChange={(value)=>handleInputChange('productRate',value)}
              disable={!isEditMode}
            />
            <Input
              columnSpan={1}
              title="Weight Rate"
              name="weightRate"
              value={profileData?.weightRate || ''}
              onChange={(value)=>handleInputChange('weightRate',value)}
              disable={!isEditMode}
            />
            <Input
              columnSpan={1}
              title="Minimum Air Weight"
              name="minimumAirWeight"
              value={profileData?.minimumAirWeight || ''}
              onChange={(value)=>handleInputChange('minimumAirWeight',value)}
              disable={!isEditMode}
            />
            <Input
              columnSpan={1}
              title="COD Rate"
              name="codRate"
              value={profileData?.codRate || ''}
              onChange={(value)=>handleInputChange('codRate',value)}
              disable={!isEditMode}
            />
            <Input
              columnSpan={1}
              title="Down Rate"
              name="downRate"
              value={profileData?.downRate || ''}
              onChange={(value)=>handleInputChange('downRate',value)}
              disable={!isEditMode}
            />
            <Input
              columnSpan={1}
              title="Credit Rate"
              name="creditRate"
              value={profileData?.creditRate || ''}
              onChange={(value)=>handleInputChange('creditRate',value)}
              disable={!isEditMode}
            />
            <Input
              columnSpan={1}
              title="Register Date"
              name="registerDate"
              disable={true}
              value={formatDate(profileData?.registerDate) || ''}
            />
            <div className="col-span-1">
              <div className="flex items-center space-x-8">
                <div className="flex items-center space-x-2">
                  <label className="mr-2 font-medium" htmlFor="isFlowRate">Is Flow Rate</label>
                  <input
                    id="isFlowRate"
                    type="checkbox"
                    checked={profileData?.isFlowRate || false}
                    onChange={(e) => handleInputChange('isFlowRate', e.target.checked)}
                    disabled={!isEditMode}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <label className="mr-2 font-medium" htmlFor="isCompanyCustomer">ลูกค้าจ่ายผ่านลิ้งค์</label>
                  <input
                    id="isCompanyCustomer"
                    type="checkbox"
                    checked={profileData?.isCompanyCustomer || false}
                    onChange={(e) => handleInputChange('isCompanyCustomer', e.target.checked)}
                    disabled={!isEditMode}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <label className="mr-2 font-medium" htmlFor="hasFacebookOrigin">มาจาก Facebook</label>
                  <input
                    id="hasFacebookOrigin"
                    type="checkbox"
                    checked={profileData?.hasFacebookOrigin || false}
                    onChange={(e) => handleInputChange('hasFacebookOrigin', e.target.checked)}
                    disabled={!isEditMode}
                  />
                </div>

              </div>
            </div>
            <Input
              columnSpan={1}
              title="Extra Flow Discount"
              name="extraFlowDiscount"
              value={profileData?.extraFlowDiscount || ''}
              onChange={(value) => handleInputChange('extraFlowDiscount', value)}
              disable={!isEditMode}
            />
            <Input
              columnSpan={1}
              title="Extra Percent Discount"
              name="extraPercentDiscount"
              value={profileData?.extraPercentDiscount || ''}
              onChange={(value) => handleInputChange('extraPercentDiscount', value)}
              disable={!isEditMode}
            />
            {shopName === 'tama' &&
              <Input
                columnSpan={1}
                title="Transaction Fee"
                name="transactionFee"
                value={profileData?.transactionFee || ''}
                onChange={(value) => handleInputChange('transactionFee', value)}
                disable={!isEditMode}
              />
            }
            <Input
              columnSpan={1}
              title="ตัดยอด จ่ายเกินมา"
              name="advanceAmount"
              value={profileData?.advanceAmount || ''}
              onChange={(value) => handleInputChange('advanceAmount', value)}
              disable={!isEditMode}
            />
              <Input
              columnSpan={2}
              title="Note"
              name="note"
              value={profileData?.note || ''}
              onChange={(value)=>handleInputChange('note',value)}
              disable={!isEditMode}
              customClass={profileData.note ? 'bg-green-300 text-black' : ''}
            />

            {feeExclusions.length > 0 && (
              <div className="col-span-full">
                <p className="text-xs font-medium text-gray-500 mb-1">ยกเว้นค่าบริการเว็บไซต์</p>
                <div className="flex flex-wrap gap-1.5">
                  {feeExclusions.map(e => (
                    <span
                      key={e.id}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-300"
                    >
                      {e.website?.webName}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <CustomerPaymentHistoryModal
        isOpen={isPaymentHistoryOpen}
        onClose={() => setIsPaymentHistoryOpen(false)}
        profile={profile}
      />
      <NegotiationQuotaModal
        isOpen={isNegotiationQuotaOpen}
        onClose={() => setIsNegotiationQuotaOpen(false)}
        profile={profile}
      />
      <CustomerChangeLogModal
        isOpen={isChangeLogOpen}
        onClose={() => setIsChangeLogOpen(false)}
        profile={profile}
      />
      <FutureRateModal
        isOpen={isRateModalOpen}
        onClose={() => setIsRateModalOpen(false)}
        profile={profile}
        accumulatedOrderAmount={accumulatedOrderAmount}
      />
      <CustomerManageModal
        isOpen={isManageModalOpen}
        onClose={() => setIsManageModalOpen(false)}
        customerId={profile?.id}
        currentEmail={profile?.User?.email}
        isActive={profile?.User?.isActive}
        isSuperuser={isSuperuser}
        onChanged={onAccountChanged}
      />
      <PromoteLinkModal
        isOpen={isPromoteLinkOpen}
        onClose={() => setIsPromoteLinkOpen(false)}
        profile={profile}
      />
      <MergeAccountModal
        isOpen={isMergeOpen}
        onClose={() => setIsMergeOpen(false)}
        profile={profile}
      />
      <CustomerRateCheckModal
        isOpen={isRateCheckOpen}
        onClose={() => setIsRateCheckOpen(false)}
        profile={profile}
        onOpenEditor={() => {
          setIsRateCheckOpen(false);
          setIsRateModalOpen(true);
        }}
      />
    </div>
  );
};

export default ProfileDetail;
